import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getSubCenterFromPath } from '@/hooks/useMenu/subCenter';

const cacheParams = sessionStorage.getItem('pathToParams');
export const pathToParams = cacheParams ? JSON.parse(cacheParams) : {};

/**
 * 获取当前路由的前缀路径
 * 根据当前路径自动判断并返回对应的前缀路径
 * @returns {string} 路由前缀路径
 */
export function getPrefix() {
  const currPathname = window.location.pathname;
  const [, var1, t, var2, c, var3, sc] = currPathname.split('/');
  let prefix = '';

  if (var1 === 'console') {
    // 租户模式: /console/{tenant}/cluster/{cluster}
    prefix = `/console/${t}/cluster/${c}`;
  } else if (var1 === 'data-center') {
    // 数据中心模式: /data-center/{cluster}
    prefix = `/data-center/${t}`;
  } else if (var1 === 'cluster' || var1 === 'wsp') {
    // 集群模块: /cluster/{cluster}
    prefix = var1 === 'wsp' ? `/wsp/${t}/cluster/${c}` : `/cluster/${t}`;
    if (var2 === 'subcluster') {
      prefix += `/subcluster/${c}`;
    } else if (var3 === 'subcluster') {
      prefix += `/subcluster/${sc}`;
    }
  } else if (var1 === 'system') {
    prefix = '/system';
  }

  const subCenter = getSubCenterFromPath(currPathname);
  if (subCenter && prefix) {
    return `${prefix}/${subCenter}`;
  }

  return prefix;
}

export function mergePrefix(path: string) {
  const prefix = getPrefix();
  if (prefix && !path.startsWith(prefix)) {
    const subCenter = getSubCenterFromPath(prefix);
    // 目标路径已自带当前子中心段时，只补作用域前缀，避免 `/app-center/app-center/...`。
    if (subCenter && path.startsWith(`/${subCenter}/`)) {
      return `${prefix.slice(0, -subCenter.length - 1)}${path}`;
    }
    return `${prefix}${path}`;
  }
  return path;
}

/** 合并 pathToParams 中该 pathname 的缓存 query，返回完整路径（供 navigate 绝对跳转） */
export function buildPathWithCachedParams(path: string): string {
  const [basePath, queryString] = path.split('?');
  const existingParams = pathToParams?.[basePath] || '';
  const combinedParams = [queryString, existingParams].filter(Boolean).join('&');

  if (!combinedParams) {
    return basePath;
  }

  const processedParams = [...new Set(combinedParams.replaceAll('/', '%2F').split('&'))].join('&');

  return `${basePath}?${processedParams}`;
}

/**
 * 增强版路由跳转Hook，支持路径自动补全和参数处理
 * 兼容react-router-dom的useNavigate所有功能
 * @returns {function} 增强版导航函数
 */
export function usePathTo() {
  const navigate = useNavigate();

  return (
    path: string | number,
    options?: {
      flag?: boolean; // 是否跳过参数处理（true=跳过，直接跳转），需要移除，命名不规范
      noCacheParams?: boolean; // 不使用缓存参数
      state?: any; // 路由状态对象
      replace?: boolean; // 是否替换当前历史记录
      preventScrollReset?: boolean; // 是否阻止滚动复位
    },
  ) => {
    // 处理数字导航（前进/后退）
    if (typeof path === 'number') {
      navigate(path);
      return;
    }

    // 解构选项参数，设置flag默认值为true（默认处理参数）
    const { flag, noCacheParams, ...rest } = options || {};

    try {
      // 分割路径和查询参数
      const [basePath, queryString] = path.split('?');

      // 获取当前路由前缀
      const prefix = getPrefix();

      // 规范化路径（自动添加前缀）
      const subCenter = getSubCenterFromPath(prefix);
      // 兼容仍以子中心段开头的既有业务路由常量。
      const normalizedPath =
        prefix && !basePath.startsWith(prefix)
          ? subCenter && basePath.startsWith(`/${subCenter}/`)
            ? `${prefix.slice(0, -subCenter.length - 1)}${basePath}`
            : `${prefix}${basePath}`
          : basePath;

      // 如果flag为false，跳过参数处理直接跳转
      if (noCacheParams || flag === false) {
        navigate(normalizedPath + (queryString ? `?${queryString}` : ''), rest);
        return;
      }

      // 获取缓存参数并合并
      const existingParams = pathToParams?.[normalizedPath] || ''; // 从缓存获取的参数
      const combinedParams = [queryString, existingParams].filter(Boolean).join('&'); // 合并URL参数和缓存参数

      // 如果没有参数需要处理，直接跳转
      if (!combinedParams) {
        navigate(normalizedPath, rest);
        return;
      }

      navigate(buildPathWithCachedParams(`${normalizedPath}${queryString ? `?${queryString}` : ''}`), rest);
    } catch (e) {
      // 出错时回退到原始导航
      navigate(path, rest);
    }
  };
}
/**
 * 操作URL参数
 */
export function useUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  useEffect(() => {
    pathToParams[pathname] = searchParams.toString();
    sessionStorage.setItem('pathToParams', JSON.stringify(pathToParams));
  }, [pathname, searchParams]);

  // 设置参数（使用 setSearchParams 函数式更新，避免 stale searchParams 与 navigate('?') 不可靠）
  const setParam = useCallback(
    (key: string, value: string | number | null) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (value === null || value === undefined || value === '') {
            newParams.delete(key);
          } else {
            newParams.set(key, value.toString());
          }
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 设置多个参数
  const setPartParams = useCallback(
    (params: Record<string, string | number | null>) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(params)) {
            if (value === null || value === undefined || value === '') {
              newParams.delete(key);
            } else {
              newParams.set(key, value.toString());
            }
          }
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 删除参数
  const delParam = useCallback((key: string) => setParam(key, null), [setParam]);

  // 获取参数
  const getParam = useCallback(
    (key: string, defaultValue?: any) => {
      return searchParams.get(key) || defaultValue;
    },
    [searchParams],
  );

  // 获取所有参数
  const getAllParams = useCallback(() => {
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  }, [searchParams]);

  // 设置所有参数
  const setAllParams = useCallback(
    (params: Record<string, string | number | null>) => {
      const newParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined && value !== '') {
          newParams.set(key, value.toString());
        }
      }
      setSearchParams(newParams);
    },
    [setSearchParams],
  );

  // 清除参数
  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  return {
    setParam,
    getParam,
    delParam,
    getAllParams,
    setAllParams,
    clearParams,
    setPartParams,
  };
}

export default useUrlParams;
